// StoredImage.js - #536. One place that draws a photograph the app has stored.
//
// WHY A COMPONENT RATHER THAN A ONE-LINE CHANGE AT EACH SCREEN: a private file
// needs a time-limited address, fetching that address is a round trip, and a
// round trip cannot happen in the middle of drawing a row. Several of the screens
// that show these photos draw them inside a list, where a React hook is not
// allowed. A component can hold its own state per row, so the list sites and the
// single-photo sites both become one line.
//
// It is deliberately drop-in: `value` takes exactly what the database column
// holds and every other prop is passed through to the image, so swapping
// `<img src={x} ... />` for `<StoredImage value={x} ... />` changes nothing else.

import React, { useState, useEffect } from 'react';
import { signedUrlFor, openStoredFile } from '../lib/storedFile';

// Fetch a time-limited address for one stored file, for a screen that needs the
// address itself - a download button, say - rather than just an image.
//
// Returns null while it is still being fetched AND when there is nothing to
// show, which is the same thing as far as every caller here is concerned: both
// mean "do not draw a photo yet".
export function useSignedUrl(value, bucket) {
  const [url, setUrl] = useState(null);

  useEffect(() => {
    // A screen can be closed, or a list re-drawn, before the address comes back.
    // Writing to state after that is the classic React memory-leak warning, and
    // on a fast list it fires constantly - so the request is abandoned instead.
    let cancelled = false;
    if (!value) { setUrl(null); return undefined; }
    signedUrlFor(value, bucket).then(u => { if (!cancelled) setUrl(u); });
    return () => { cancelled = true; };
  }, [value, bucket]);

  return url;
}

export default function StoredImage({
  value,
  bucket,
  alt = '',
  // Open the full picture in a new tab when the image is clicked. The address is
  // fetched fresh at the click rather than reused, so a tab opened an hour later
  // still works and nothing long-lived sits in the page.
  openOnClick = false,
  onClick,
  fallback = null,
  ...rest
}) {
  const url = useSignedUrl(value, bucket);

  // Nothing stored, or the address could not be fetched. Draw whatever the
  // screen drew before when there was no photo - usually nothing at all. This
  // is the branch that keeps a missing photo from taking the page down with it.
  if (!url) return fallback;

  function handleClick(e) {
    if (onClick) onClick(e);
    if (openOnClick) openStoredFile(value, bucket);
  }

  return (
    <img
      src={url}
      alt={alt}
      onClick={openOnClick || onClick ? handleClick : undefined}
      {...rest}
    />
  );
}
